import type { MouseEvent } from 'react';
import { useSyncExternalStore } from 'react';

/**
 * FASE 121 (121.B.1) — roteador client-side MINIMO (History API), sem dep de lib externa (coerente com o
 * comentario "Evita uma dep de router so para 1 rota"). Substitui a leitura UNICA de `window.location` no
 * boot (`main.tsx`): antes toda troca deck <-> /chat <-> /chat?master=1 era um `<a href>` puro = RELOAD
 * COMPLETO do browser (re-download do bundle + reconexao WS de cada terminal + xterm re-montado do zero).
 * Com este roteador a troca e client-side (instantanea, sem reload).
 *
 * Snapshot = string `pathname+search` (igualdade barata p/ useSyncExternalStore). `navigate` usa
 * pushState/replaceState + notifica; `popstate` (back/forward do browser) tambem notifica.
 */
const listeners = new Set<() => void>();
let popNavigation = false;
export const navigationWasPopstate = (): boolean => popNavigation;

function emit(): void {
  for (const l of listeners) l();
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

function getSnapshot(): string {
  if (typeof window === 'undefined') return '/';
  return window.location.pathname + window.location.search;
}

if (typeof window !== 'undefined') {
  window.addEventListener('popstate', () => { popNavigation = true; emit(); });
}

/** Navega SEM reload (History API). No-op se a URL destino ja e a atual (evita empilhar entradas iguais). */
export function navigate(to: string, opts?: { replace?: boolean }): void {
  if (typeof window === 'undefined') return;
  const target = new URL(to, window.location.origin);
  const next = target.pathname + target.search;
  const current = window.location.pathname + window.location.search;
  if (next === current) return;
  popNavigation = false;
  if (opts?.replace) window.history.replaceState(null, '', next);
  else window.history.pushState(null, '', next);
  emit();
}

/** Hook: href atual (`pathname+search`), reativo a navegacao client-side E ao back/forward do browser. */
export function useRouteHref(): string {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/**
 * onClick para `<a href>` que faz navegacao client-side no clique ESQUERDO simples, PRESERVANDO o href:
 * ctrl/cmd/shift/alt-click e clique do meio caem no comportamento nativo do browser (abrir em nova aba/
 * janela — 121.B.3), e o `href` mantem a acessibilidade e o "abrir em nova aba" do menu de contexto.
 */
export function onNavClick(
  to: string,
  extra?: () => void,
): (e: MouseEvent<HTMLAnchorElement>) => void {
  return (e: MouseEvent<HTMLAnchorElement>): void => {
    if (e.defaultPrevented) return;
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    navigate(to);
    extra?.();
  };
}
